/* eslint-disable no-useless-escape */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Divider, Input, Modal, Spin, Tooltip } from 'antd';
import {
  ContactIcon,
  ErrorIcon,
  NewCloseIcon,
  NewRemoveIcon,
  NewUpdateIcon,
} from '../Common/Icons';
import {
  Actions,
  PhotoResolution,
  RoleType,
  UserRoles,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import {
  formatFullNameDisplay,
  isCare,
  isManage,
  isSocial,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import useUserInfo from '../Hooks/useUserInfo';
import useContacts from '@/Components/Hooks/useContacts';
import NewCustomButton, { ButtonTypes } from '../Common/NewCustomButton';
import Roles from '../Roles/Roles';
import { useCallback } from 'react';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { emailPattern } from '@/utilities/helpers/commonHelper';
import './Contacts.less';

const RequiredFields = {
  firstname: 'First Name',
  lastname: 'Last Name',
  email: 'Email',
};

const DefaultValues = {
  firstname: '',
  lastname: '',
  email: '',
  sourcerelationship: '',
  targetrelationship: '',
};

const formatData = (data, isEditForm, role) => {
  const userObj = { rolename: role };
  Object.keys(DefaultValues).forEach((key) => (userObj[key] = data[key]));
  if (isEditForm) {
    userObj.useruuid = data.useruuid;
  }
  return userObj;
};

const ContactForm = (props) => {
  const { parentBread, hubRecord, record, action, onActionClick } = props;

  const { data: contacts = [], isFetched: contactsFetched } = useContacts({
    useruuid: hubRecord?.useruuid,
  });

  const [role, setRole] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const trans = useSelector(translator);
  const userApi = useSelector((state) => state.apis.userApi);
  const shareResourcesApi = useSelector((state) => state.apis.shareResourcesApi);
  const currentUser = useSelector(getCurrentUser);

  const isEditForm = action === Actions.editContact;
  const header = (parentBread || []).concat({
    label: `${isEditForm ? trans('EDIT') : trans('ADD')} ${trans('CONTACT')}`,
  });

  const currentRole = useMemo(
    () =>
      isManage(record)
        ? UserRoles.manage
        : isCare(record)
          ? UserRoles.care
          : isSocial(record)
            ? UserRoles.social
            : null,
    [record]
  );
  const [isValid, setIsValid] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState(DefaultValues);
  const image = useMemo(
    () => hubRecord?.profilePics && hubRecord?.profilePics[PhotoResolution.R64],
    [hubRecord]
  );

  const { data: userInfo = {}, isFetching: fetchingUserInfo } = useUserInfo({
    useruuid: isEditForm && record?.useruuid,
  });
  const [emailError, setEmailError] = useState('');
  const [nameFieldError, setNameFieldError] = useState('');

  // Recompute form validity when inputs or async email validation prerequisites change.
  useEffect(() => {
    validate(false);
  }, [formData, role, emailError, contactsFetched, isEditForm]);

  useEffect(() => {
    const emailValue = formData?.email?.trim();

    if (!emailValue) {
      setEmailError('');
      return;
    }

    if (!emailPattern.test(emailValue)) {
      setEmailError(trans('PLEASEENTERAVALIDEMAILADDRESS'));
      return;
    }

    const normalizedEmail = emailValue.toLowerCase();
    const duplicateEmail = contacts.some((contact) => {
      const contactEmail = contact?.email?.toLowerCase();
      if (contactEmail !== normalizedEmail) return false;
      if (isEditForm && contact?.useruuid === record?.useruuid) return false;
      return true;
    });

    setEmailError(duplicateEmail ? trans('EXISTINGCONTACTPLEASEENTERDIFFERNTEMAILADDRESS') : '');
  }, [contacts, formData?.email, isEditForm, record?.useruuid, trans]);

  useEffect(() => {
    isEditForm &&
      setFormData({
        ...userInfo,
        sourcerelationship: record?.sourcerelationship,
        targetrelationship: record?.targetrelationship,
      });
  }, [userInfo, isEditForm, record]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    if (name === 'firstname' || name === 'lastname') {
      setNameFieldError('');
    }
    if (name === 'email') {
      const isValidEmail = emailPattern.test(value);
      // check if duplicate email is entered
      if (isValidEmail) {
        const duplicateEmail = contacts.some(
          (contact) => contact?.email === value && contact?.email !== formData.email
        );
        setEmailError(
          duplicateEmail ? trans('EXISTINGCONTACTPLEASEENTERDIFFERNTEMAILADDRESS') : ''
        );
      } else {
        setEmailError(trans('PLEASEENTERAVALIDEMAILADDRESS'));
      }
    }
    setFormData({ ...formData, [name]: value });
  };

  const validate = (showError = true) => {
    const values = formData;
    let valid = true;
    let erroredFields = [];
    for (let field of Object.keys(RequiredFields)) {
      if (!values.hasOwnProperty(field) || !values[field]) {
        erroredFields.push(RequiredFields[field]);
        valid = false;
      }
    }
    // Check if a role is selected
    if (!role) {
      erroredFields.push('Role');
      valid = false;
    }
    if (showError && !valid) {
      let error = 'Please fill required fields: ' + erroredFields.join(', ');
      //message.error(error, 10);
      console.error(error, 10);
    }
    const canSubmit = valid && !emailError && (isEditForm || contactsFetched);
    setIsValid(canSubmit);
    return canSubmit;
  };

  const handleSubmit = () => {
    const errorCallback = (error) => {
      setProcessing(false);
      const msg = `Error ${isEditForm ? 'updating' : 'adding'} contact`;
      console.error(msg, error);
      setNameFieldError(error?.info || msg);
    };
    const successCallback = (data) => {
      setProcessing(false);
      const msg = `Contact ${isEditForm ? 'updated' : 'added'} successfully!`;
      onActionClick(isEditForm ? Actions.editContact : null, isEditForm ? record : null, true)();
    };
    try {
      // Trim leading and trailing whitespace from formData
      const trimmedFormData = {
        ...formData,
        firstname: formData.firstname.trim(),
        lastname: formData.lastname.trim(),
        sourcerelationship: formData?.sourcerelationship?.trim(),
        targetrelationship: formData?.targetrelationship?.trim(),
      };
      const valid = validate();
      if (!valid) return;
      if (!isEditForm) {
        const duplicateEmail = contacts.some((contact) => contact?.email === formData?.email);
        if (duplicateEmail) {
          // message.error(trans("EMAILALREADYINUSEMSG"));
          console.error('Email already in use. Please try a different email.');
          return;
        }
      }
      setProcessing(true);
      const obj = formatData(trimmedFormData, isEditForm, role);
      userApi.inviteContactsToUser(
        hubRecord?.useruuid,
        obj,
        apiCallback({
          translator: trans,
          successCallback,
          errorCallback,
          failCallback: errorCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const handleRoleSelection = useCallback((role) => {
    setRole(role);
  }, []);

  // Render form item with label, input field, and error handling
  const renderFormItem = ({ name, label, disabled, placeholder, className }) => (
    <div className={`form-item form-item-verticle ${className ?? ''}`}>
      <label>{label}</label>
      <div className={`input-container ${name === 'email' && emailError ? 'error' : 'default'}`}>
        <Input
          className="input-content"
          name={name}
          placeholder={placeholder}
          value={formData[name]}
          onChange={handleInputChange}
          disabled={disabled}
        />
      </div>
      <div className="error-text">
        {name === 'email' && emailError && (
          <span className="error-message">
            <ErrorIcon className="error-icon" fillColor="#F46363" />
            {emailError}
          </span>
        )}
      </div>
    </div>
  );

  // Render role selection
  const renderRole = ({ label, className }) => {
    const isCurrentUserEditing = currentUser && currentUser.useruuid === record?.useruuid;
    return (
      <div className={`form-item form-item-verticle ${className ?? ''}`}>
        <label>{label}</label>
        <Roles
          contact={true}
          type={RoleType.user}
          onHandleSelection={handleRoleSelection}
          currentRole={currentRole}
          disabled={isCurrentUserEditing}
        />
      </div>
    );
  };

  // Check if user type and if email is pending then do not show remove all option.
  const isPrivateManageUser = isEditForm && record?.emailVerified !== false;

  const handleRemoveAll = () => {
    if (!hubRecord?.useruuid || !record?.useruuid) {
      // message.error("Missing user information.");
      console.error('Missing user information.');
      return;
    }

    const errorCallback = (error) => {
      console.error('Error unsharing resources', error);
      // message.error("Failed to remove shared content.");
    };

    const successCallback = (data) => {
      // message.success("All shared content removed successfully!");
    };

    const userUuid = hubRecord?.useruuid; // Principal account UUID
    const payload = {
      contactuseruuid: [record.useruuid], //useruuid of contact
    };
    try {
      shareResourcesApi.unshareAllResources(
        userUuid,
        payload,
        apiCallback({
          translator: trans,
          successCallback,
          errorCallback,
          failCallback: errorCallback,
        })
      );
    } catch (error) {
      console.error('error', error);
    }
  };

  const showRemoveAllModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleConfirmRemoveAll = () => {
    handleRemoveAll();
    setIsModalVisible(false);
  };

  // Main return JSX for the Contact Form component
  return (
    <div className="add-contact-container">
      <div className="add-contact-header">
        <div className="icon">
          <ContactIcon style={{ fill: '#113E57', width: '30px', height: '30px' }} />
        </div>
        <div className="header">
          {isEditForm ? trans('EDIT') : trans('ADD')} {trans('CONTACT')}
        </div>
      </div>
      <Spin spinning={processing || fetchingUserInfo}>
        <div className="add-contact-block">
          <div className="relationship-container">
            <div className="label">{trans('RELATIONSHIP')}</div>
            <div className="relationship-form-item">
              <div className="relationship-details">
                <div className="form-input-container">
                  {renderFormItem({
                    name: 'sourcerelationship',
                    label: (
                      <>
                        <span>
                          <Tooltip title={`${hubRecord?.firstname} ${hubRecord?.lastname}`}>
                            <span>
                              {`${formatFullNameDisplay(
                                hubRecord?.firstname,
                                hubRecord?.lastname
                              )}'s `}
                            </span>
                          </Tooltip>
                          {`${trans('RELATIONSHIPWITHTHE')} ${
                            isEditForm ? trans('CONTACT') : trans('NEW_L_CONTACT_U')
                          }`}
                        </span>
                      </>
                    ),
                    placeholder: `${trans('ENTERRELATIONSHIPWITHTHE')} ${
                      isEditForm ? trans('CONTACT_L') : trans('NEWCONTACT_L')
                    }`,
                    // placeholder: "Friend, sister, brother, neighbor, etc.",
                  })}
                </div>
              </div>
            </div>
          </div>
          <Divider className="divider-style" />
          <div className="contact-container">
            <div className="new-contact-container">
              <div className="label">{isEditForm ? trans('CONTACT') : trans('NEWCONTACT_U')}</div>
              <div className="new-contact-form-item">
                <div className="details-1">
                  <div className="form-input-container">
                    {renderFormItem({
                      name: 'firstname',
                      label: `${trans('FIRSTNAME')}*`,
                      placeholder: trans('ENTERFIRSTNAME'),
                      disabled: isEditForm,
                    })}
                  </div>
                  <div className="form-input-container">
                    {renderFormItem({
                      name: 'lastname',
                      label: `${trans('LASTNAME')}*`,
                      placeholder: trans('ENTERLASTNAME'),
                      disabled: isEditForm,
                    })}
                  </div>
                </div>
                {nameFieldError && (
                  <div className="details-1 details-1-name-error">
                    <div className="name-field-error">{nameFieldError}</div>
                  </div>
                )}
                <div className="details-1">
                  <div className="form-input-container">
                    {renderFormItem({
                      name: 'targetrelationship',
                      label: (
                        <>
                          <span>
                            {`${
                              isEditForm ? trans('CONTACT_S_U') : trans('NEWCONTACTS_S_U')
                            } ${trans('RELATIONSHIPWITH')} `}
                            <Tooltip title={`${hubRecord?.firstname} ${hubRecord?.lastname}`}>
                              <span>
                                {formatFullNameDisplay(hubRecord?.firstname, hubRecord?.lastname)}
                              </span>
                            </Tooltip>
                          </span>
                        </>
                      ),
                      placeholder: `${trans('ENTER')} ${
                        isEditForm ? trans('CONTACT_L') : trans('NEWCONTACT_L')
                      } ${trans('RELATIONSHIP_L')}`,
                      // placeholder: "Friend, sister, brother, neighbor, etc.",
                    })}
                  </div>
                  <div className="form-input-container">
                    {renderFormItem({
                      name: 'email',
                      label: `${trans('EMAIL')}*`,
                      placeholder: trans('ENTEREMAILADDRESS'),
                      disabled: isEditForm,
                    })}
                  </div>
                </div>
                <div className="role-container-1">
                  <div className="label">
                    <span>
                      {` ${trans('WHATROLEDOESTHISPERSONHAVEWITH')} `}
                      <Tooltip title={`${hubRecord?.firstname} ${hubRecord?.lastname}`}>
                        <span>
                          {formatFullNameDisplay(hubRecord?.firstname, hubRecord?.lastname)}
                        </span>
                      </Tooltip>
                      ?*
                    </span>
                  </div>
                  <div className="roles-list">{renderRole({})}</div>
                </div>
                {isPrivateManageUser && (
                  <div className="shared-content-container">
                    <span className="shared-content-title">{trans('SHAREDGALLERYCONTENT')}</span>
                    <span className="shared-content-remove-container">
                      <span>
                        <NewRemoveIcon />
                      </span>
                      <span className="shared-content-remove-button" onClick={showRemoveAllModal}>
                        {trans('REMOVEALL')}
                      </span>
                    </span>
                  </div>
                )}
                <div className="buttons-container">
                  <div className="new-cancel-button">
                    <NewCustomButton
                      label={trans('CANCEL')}
                      icon={<NewCloseIcon className="cancel-icon" />}
                      onClick={onActionClick(null, null)}
                    />
                  </div>
                  <div className="update-button">
                    <NewCustomButton
                      type={ButtonTypes.primary}
                      label={isEditForm ? trans('UPDATE') : trans('ADD')}
                      icon={
                        <NewUpdateIcon
                          className={isValid ? 'update-icon-enabled' : 'update-icon-disabled'}
                        />
                      }
                      onClick={handleSubmit}
                      disabled={!isValid}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Spin>
      <Modal
        open={isModalVisible}
        onOk={handleConfirmRemoveAll}
        onCancel={handleCancel}
        closable={false}
        footer={null}
        mask={true}
        centered
        className="remove-shared-content-pop-up-container"
      >
        <div className="remove-all-shared-gallery-info">
          <span className="remove-shared-content-title">{trans('REMOVEALLSHAREDCONTENT')}</span>
          <p className="remove-shared-content-description">
            {trans('REMOVEALLSHAREDCONTENTDESCRIPTION')}
          </p>
        </div>
        <div className="remove-shared-footer-buttons">
          <Button className="custom-no-button" onClick={handleCancel}>
            <span className="custom-no-label">{trans('CANCEL')}</span>
          </Button>
          <Button
            className="custom-yes-button"
            onClick={() => {
              handleConfirmRemoveAll();
              handleCancel(); // Close the modal after confirmation
            }}
          >
            <span className="custom-yes-label">{trans('PROCEEDREMOVEALL')}</span>
          </Button>
        </div>
      </Modal>
    </div>
  );
};

ContactForm.propTypes = {
  parentBread: PropTypes.arrayOf(PropTypes.object),
  hubRecord: PropTypes.object,
  record: PropTypes.object,
  action: PropTypes.string,
  onActionClick: PropTypes.func,
};

export default ContactForm;
